import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star, Send, Copy, RefreshCw, ArrowLeft, Lightbulb, MessageSquare, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import { businessTypes } from '../mock';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';

const sampleReviews = [
  {
    rating: 5,
    text: 'Absolutely brilliant service! Sarah went above and beyond to help find the perfect bouquet for my mum’s birthday. Will definitely be back.',
  },
  {
    rating: 2,
    text: 'Waited 45 minutes for our order. Food was fine when it arrived but the delay made the whole evening rushed. Disappointed.',
  },
  {
    rating: 4,
    text: 'Great haircut, very friendly staff. Only knocking a star off because parking was tricky.',
  },
];

const Reviews = () => {
  const navigate = useNavigate();
  const [review, setReview] = useState('');
  const [rating, setRating] = useState(5);
  const [reviewerName, setReviewerName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('other');
  const [tone, setTone] = useState('friendly');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!review.trim()) {
      toast({ title: 'Paste a review first' });
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/reviews/respond', {
        review, rating, reviewerName, businessName, businessType, tone,
      });
      setResult(data);
    } catch (err) {
      toast({ title: 'Could not generate', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.reply);
    toast({ title: 'Reply copied' });
  };

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">AI Review Responder</h1>
            <p className="mt-1 text-slate-600">Paste any customer review (Google, Facebook, anywhere) and get a perfectly worded reply.</p>
          </div>
          <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Works for any platform
          </Badge>
        </div>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
            <div className="space-y-2">
              <Label>Reviewer's name <span className="text-slate-400 font-normal">(optional — makes reply personal)</span></Label>
              <Input value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} placeholder="e.g. Olivia, or Olivia G." className="h-11" />
            </div>

            <div className="space-y-2">
              <Label>Customer review</Label>
              <Textarea value={review} onChange={(e) => setReview(e.target.value)} placeholder="Paste the review text here…" className="min-h-[140px] resize-none" />
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-xs text-slate-400 mr-1">Try sample:</span>
                {sampleReviews.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { setReview(s.text); setRating(s.rating); }}
                    className="text-xs bg-slate-100 hover:bg-orange-100 hover:text-orange-700 px-2.5 py-1 rounded-full transition-colors flex items-center gap-1"
                  >
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {s.rating}★
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Star rating</Label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setRating(n)} className="p-0.5">
                      <Star className={`w-7 h-7 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reply tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly &amp; warm</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="apologetic">Apologetic (for complaints)</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Business name</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Bloom Florists" className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Business type</Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {businessTypes.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-semibold rounded-xl text-base"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Crafting reply…</>
              ) : (
                <><Send className="w-5 h-5 mr-2" /> Generate AI reply</>
              )}
            </Button>
          </div>

          {/* Result */}
          <div className="space-y-4">
            {!result && !loading && (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center">
                  <MessageSquare className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-slate-900">Your reply will appear here</h3>
                <p className="mt-1 text-slate-500 max-w-sm">Diplomatic, on-brand, ready to copy into Google or Facebook.</p>
              </div>
            )}
            {loading && (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
                <Loader2 className="w-10 h-10 mx-auto text-orange-500 animate-spin" />
                <h3 className="mt-4 text-xl font-bold text-slate-900">Writing your reply…</h3>
              </div>
            )}
            {result && (
              <>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">AI-generated reply</div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={handleCopy}>
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleGenerate}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate
                      </Button>
                    </div>
                  </div>
                  <div className="text-slate-800 leading-relaxed whitespace-pre-line">{result.reply}</div>
                </div>

                {result.suggestions?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-amber-700" />
                      <div className="text-sm font-semibold text-amber-900">Follow-up tips</div>
                    </div>
                    <ul className="space-y-1.5">
                      {result.suggestions.map((s, i) => (
                        <li key={i} className="text-sm text-amber-900">• {s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
